<template>
    <div class="phi-type-editor">

        <phi-drawer :open="!editorOpen && !iconDrawer">
            <div class="face phi-media" @click="editorOpen = true">
                <div class="phi-media-figure">
                    <img v-if="type.icon" :src="type.icon" alt="type.singular" />
                    <mu-icon v-else value="add" :size="40"></mu-icon>
                </div>
                <h3 class="phi-media-body">
                    <template v-if="type.singular">{{ $t(type.singular) }}</template>
                    <template v-else>{{ label }}</template>
                </h3>
            </div>
        </phi-drawer>

        <phi-drawer :open="editorOpen && !iconDrawer">
            <div class="editor">
                <div class="phi-media">
                    <div class="phi-media-figure" @click="iconDrawer = !iconDrawer">
                        <img v-if="type.icon" :src="type.icon" alt="type.singular" />
                        <mu-icon v-else value="help" class="empty" :size="40"></mu-icon>
                    </div>
                    <div class="phi-media-body">
                        <div class="type-name">

                            <select v-model="type.gender">
                                <option :value="1">{{ $t('noun.nounPersonMale') }}</option>
                                <option :value="0">{{ $t('noun.nounPersonFemale') }}</option>
                            </select>
                                                   
                            <ui-input  
                                :label="$t('noun.nameSingular')" 
                                v-model="type.singular"  
                                @input="sanitize($event, 's')"  
                                @paste.prevent="handlePaste" 
                                :disabled="!isEditable" 
                                @keydown="sanitize($event, 's')" 
                                id="singular"
                            ></ui-input>
                            
                            <ui-input 
                                :label="$t('noun.namePlural')" 
                                v-model="type.plural" 
                                @input="sanitize($event, 'p')" 
                                @keydown="sanitize($event, 'p')" 
                                @paste.prevent="handlePastePlural" 
                                id="plural"
                            ></ui-input>
                        </div>
                    </div>
                </div>
                <div style="color: #000; opacity: 35%;" v-if="isEmpty">
                    <small>{{ $t('infoMsg') }}</small>
                </div>
                <div class="actions">
                    <button type="button" class="ok" @click="save" :disabled="isEmpty">{{ $t('OK') }}</button>
                    <button type="button" class="cancel" @click="cancel">{{ $t('action.cancel') }}</button>
                </div>
            </div>
        </phi-drawer>

        <phi-drawer :open="iconDrawer">
            <div class="icon-picker-drawer">
                <phi-icon-picker class="icon-picker" v-model="type.icon" @input="iconDrawer = false" @color="type.color = arguments[0]"></phi-icon-picker>
            </div>
        </phi-drawer>

    </div>
</template>

<script>
import PhiIconPicker from '../Icon/Picker.vue';
import { UiInput } from '@/modules/ui/components';

export default {
    name: 'phi-type-editor',
    components: {PhiIconPicker,UiInput},
    props: {
        value: {
            type: Object,
            required: true
        },

        label: {
            type: String
        }
    },

    data() {
        return {
            type: Object.assign({}, this.value),
            initialValue: Object.assign({}, this.value),

            iconDrawer: false,
            editorOpen: false,
            noEdit: ['notificacion','tarea','reunion','cobro','pago','mensaje','comunicado','seguimiento','ausencia','boletin','clasificado','novedad de transporte','proceso','examen', 'SMS']
        };
    },

    computed: {
        isEmpty() {

            return this.type.singular == null || !this.type.singular.trim() || this.type.plural == null || !this.type.plural.trim() || !this.type.icon;
        },
        isEditable() {
            return !this.noEdit.includes(this.type.singular);
        }
    },

    methods: {
        save() {
            this.editorOpen = false;
            this.iconDrawer = false;
            this.$emit("input", this.type);
        },

        cancel() {
            this.editorOpen = false;
            this.iconDrawer = false;
            this.type = Object.assign({}, this.initialValue);
            this.$emit("cancel");
        },


		handlePaste(event) {
            
            const pastedData = event.clipboardData.getData('text/plain');
            this.type.singular = pastedData; // Agregamos el texto pegado al valor actual
            
            this.sanitize(event, "s"); // Validamos el nuevo valor
        },

        handlePastePlural(event) {
            
            const pastedData = event.clipboardData.getData('text/plain');
            this.type.plural = pastedData; // Agregamos el texto pegado al valor actual
            
            this.sanitize(event, "p"); // Validamos el nuevo valor
        },

        sanitize(event, type) {
            
            const allowedChars = /^[A-Za-z0-9 \s\-()]+$/;

            if(this.type.singular != "undefined" && type == "s")
            {
                const singularInput = document.querySelector('#singular input');
                
                if (!allowedChars.test(this.type.singular)) {
                    
                    this.type.singular = "";
                    singularInput.value = "";
                    singularInput.setCustomValidity(this.$t("tooltipMessageText"));
                    singularInput.reportValidity();
                } else {
                    singularInput.setCustomValidity('');
                }
            }

            if(this.type.plural != "undefined" && type == "p")
            {
                const pluralInput = document.querySelector('#plural input');

                if (!allowedChars.test(this.type.plural)) {
                    
                    this.type.plural = "";
                    pluralInput.value = "";
                    pluralInput.setCustomValidity(this.$t("tooltipMessageText"));
                    pluralInput.reportValidity();
                } else {
                    pluralInput.setCustomValidity("");
                }
            }
        },

        sanitizer(event) {
            if(!/^[A-Za-z0-9 \s]+$/g.test(event.key)){
				event.preventDefault();
			}
        }
    },

    watch: {
        value(newValue, oldValue) {
            this.type         = Object.assign({}, this.value);
            this.initialValue = Object.assign({}, this.value);
        }
    },

    i18n: {
        "en": {
            "tooltipMessageText": "The only characters allowed to customize the message type name are the following: (), -",
            "infoMsg" : "To create a new type please select an icon and enter the singular and plural field name."
        },

        "de": {
            "tooltipMessageText": "Die einzigen Zeichen, die zum Anpassen des Namens des Nachrichtentyps zulässig sind, sind die folgenden: (), -",
            "infoMsg" : "Um einen neuen Typ zu erstellen, wählen Sie bitte ein Symbol aus und geben Sie den Feldnamen im Singular und Plural ein."
        },

        "es": {
            "tooltipMessageText": "Los únicos caracteres permitidos para personalizar el nombre del tipo de mensaje son los siguientes: (), -",
            "infoMsg" : "Para crear un nuevo tipo por favor seleccione un icono e ingrese el nombre del campo singular y plural."
        },

        "fr": {
            "tooltipMessageText": "Les seuls caractères autorisés pour personnaliser le nom du type de message sont les suivants : (), -",
            "infoMsg" : "Pour créer un nouveau type, veuillez sélectionner une icône et saisir le nom du champ au singulier et au pluriel."
        },

        "it": {
            "tooltipMessageText": "Gli unici caratteri consentiti per personalizzare il nome del tipo di messaggio sono i seguenti: (), -",
            "infoMsg" : "Per creare un nuovo tipo seleziona un'icona e inserisci il nome del campo singolare e plurale."
        },

        "jp": {
            "tooltipMessageText": "メッセージタイプ名をカスタマイズできる文字は次の文字のみです: ()、-",
            "infoMsg" : "新しいタイプを作成するには、アイコンを選択し、単数形と複数形のフィールド名を入力してください。"
        }
    }
}
</script>

<style lang="scss">
.phi-type-editor {

    .face {
        align-items: center;
        font-size: 20px;
        cursor: pointer;
    }

    & > .phi-media-figure {
        min-width: 40px;
    }

    .type-name {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        position: relative;
        
        justify-content: space-between;

        .ui-input {
            flex: 1;
            margin: 0 1em;
            max-width: 400px;
        }

        .ui-field {
            flex: 1;
            margin: 0 1em;
            min-width: 150px;
        }
    }

    .editor {
        .empty {
            cursor: pointer;
            color: red;
            opacity: .6;

            &:hover {
                opacity: .8;
            }
        }
    }

    .actions {
        display: flex;

        button {
            flex: 1;
            cursor: pointer;
            margin: none;
            padding: 12px 18px;
            border: none;
            background-color: transparent;
            font-size: 1em;
            text-align: left;

            &:hover {
                background-color: rgba(0,0,0,.04);
            }
        }

        .cancel {
            margin-left: 20px;
            background: transparent;
            border: none;
            color: #444;
        }
    }
}
</style>